import React, { useEffect } from "react";
import { Form, Input, Button, Select, Row, Col, Typography, Divider } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { editCard } from "api/user.api";
import { set } from "lodash";
import { notificationController } from "components/Common/Notification/Notification";
import CustomLabel from "components/Common/Label/CustomLabel";


interface SubscriptionEditPaymentMethodProps {
    type: string;
    cardMethod: any;
}


const { Title, Text } = Typography;
const { Option } = Select;

const SubscriptionEditPaymentMethod: React.FC<SubscriptionEditPaymentMethodProps> = ({ type, cardMethod }) => {
    const [cardDetails, setCardDetails] = React.useState<any>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [cardNumber, setCardNumber] = React.useState<string>("");
    const [expiryMonth, setExpiryMonth] = React.useState<string>("");
    const [expiryYear, setExpiryYear] = React.useState<string>("");
    const [cvv, setCvv] = React.useState<string>("");
    const [cardName, setCardName] = React.useState<string>("");
    const [email, setEmail] = React.useState<string>("");
    const [address, setAddress] = React.useState<string>("");
    const [city, setCity] = React.useState<string>("");
    const [state, setState] = React.useState<string>("");
    const [country, setCountry] = React.useState<string>("");
    const [cardError, setCardError] = React.useState<string>("");
    const [cardLogo, setCardLogo] = React.useState<string>("");
    const [methodId, setMethodId] = React.useState<string>("");

    const editCardDetails = async () => {
        const payload = {
            payment_method_id: methodId,
            card_details: {
                exp_month: Number(expiryMonth),  // Ensure it's a number
                exp_year: Number(expiryYear)
            }
        };
        try {
            const response = await editCard(payload);
            notificationController.success({
                message: response?.message
            });
            console.log("Card Details Edited: ", response);
        } catch (error) {
            console.error("Error editing card details: ", error);
            notificationController.error({
                message: `${error}`
            });
        }
    };

    const handleExpiry = (expiry: any) => {
        const [month, year] = expiry.split(" / ");
        setExpiryMonth(month || "");
        setExpiryYear(year || "");
    };

    useEffect(() => {
        if (cardMethod) {
            setMethodId(cardMethod.id);
            setCardDetails(cardMethod);
            setCardNumber(cardMethod.ending);
            setExpiryMonth(cardMethod.card.exp_month);
            setExpiryYear(cardMethod.card.exp_year);
            setCvv(cardMethod.cvv);
            setCardName(cardMethod.card_name);
            setEmail(cardMethod.email);
            setAddress(cardMethod.address);
            setCity(cardMethod.city);
            setState(cardMethod.state);
            setCountry(cardMethod.country);
            setCardLogo(cardMethod.logo);
        }
    }, [cardMethod]);

    const getChar = (s: any, n: any) => s.slice(-n);

    return (
        <div style={{ maxWidth: '95%', margin: "0 auto", padding: 24, background: "#fff", borderRadius: 8 }}>
            {/* Header */}
            <Title level={4} style={{ marginBottom: 1, color: "#101828", fontFamily: "Inter" }}>Payment method</Title>
            <Text type="secondary" style={{ color: "#475467", fontFamily: "Inter" }}>{type === "edit" ? "Update" : "Add"} your billing details and address.</Text>
            <Divider style={{ margin: '10px 0', backgroundColor: '#f5f5f5' }} />
            <Form layout="vertical">
                {/* Card Details */}
                <Row align="middle" gutter={16}>
                    <Col span={8} style={{ marginBottom: "12%" }}>
                        <Text strong style={{fontFamily: "Inter", color: "#344054"}}>Card Details <span style={{ color: "#7F56D9" }}>*</span></Text>
                    </Col>
                    <Col span={16}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item>
                                    <CustomLabel
                                        labelTitle="Name on Card"
                                        isHelpIconRequired={false}
                                        isRequired={true}
                                        tooltipText="The specific job title you will be sourcing for."
                                    />
                                    <Input placeholder="Olivia Rhye" value={cardName} onChange={(e) => { setCardName(e.target.value); }} readOnly />
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item>
                                    <CustomLabel
                                        labelTitle="Expiry"
                                        isHelpIconRequired={false}
                                        isRequired={true}
                                        tooltipText="The specific job title you will be sourcing for."
                                    />
                                    <Input placeholder="MM / YYYY" value={`${expiryMonth} / ${expiryYear}`} onChange={(e) => { handleExpiry(e.target.value); }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item>
                                    <CustomLabel
                                        labelTitle="Card Number"
                                        isHelpIconRequired={false}
                                        isRequired={true}
                                        tooltipText="The specific job title you will be sourcing for."
                                    />
                                    <Input
                                        placeholder="1234 1234 1234 1234"
                                        prefix={<img src={cardLogo} alt="Mastercard" style={{ width: 20, marginRight: 8 }} />}
                                        value={`**** **** **** ${cardNumber}`}
                                        onChange={(e) => { setCardNumber(getChar(e.target.value, 4)); }}
                                        readOnly
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item>
                                    <CustomLabel
                                        labelTitle="CVV"
                                        isHelpIconRequired={false}
                                        isRequired={true}
                                        tooltipText="The specific job title you will be sourcing for."
                                    />
                                    <Input.Password placeholder="•••" value={cvv} onChange={(e) => { setCvv(e.target.value); }} readOnly />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Divider style={{ margin: '10px 0', backgroundColor: '#f5f5f5' }} />

                {/* Email Address */}
                <Row align="middle" gutter={16}>
                    <Col span={8}>
                        <Text strong style={{fontFamily: "Inter", color: "#344054"}}>Email Address <span style={{ color: "#7F56D9" }}>*</span></Text><br />
                        {/* <Text type="secondary">{ type === "edit" ? "Update" : "Add" } your billing details and address.</Text> */}
                        <Text type="secondary" style={{color: "#475467", fontFamily: "Inter"}}>Invoices will be sent to this email address.</Text>
                    </Col>
                    <Col span={10}>
                        <Form.Item>
                            <Input readOnly prefix={<MailOutlined />} placeholder="billing@untitledui.com" value={email} onChange={(e) => { setEmail(e.target.value); }} />
                        </Form.Item>
                        <Text style={{ color: "#475467", cursor: "pointer", display: "block", marginTop: 4, fontWeight: "bold", fontFamily: "Inter" }}>+ Add another</Text>
                    </Col>
                </Row>
                <Divider style={{ margin: '10px 0', backgroundColor: '#f5f5f5' }} />

                {/* Address */}
                <Row align="middle" gutter={16}>
                    <Col span={8}>
                        <Text strong style={{fontFamily: "Inter", color: "#344054"}}>Street Address <span style={{ color: "#7F56D9" }}>*</span></Text>
                    </Col>
                    <Col span={10}>
                        <Form.Item>
                            <Input readOnly placeholder="100 Smith Street" value={address} onChange={(e) => setAddress(e.target.value)} />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider style={{ margin: '10px 0', backgroundColor: '#f5f5f5' }} />

                <Row align="middle" gutter={16}>
                    <Col span={8}>
                        <Text strong style={{fontFamily: "Inter", color: "#344054"}}>City <span style={{ color: "#7F56D9" }}>*</span></Text>
                    </Col>
                    <Col span={10}>
                        <Form.Item>
                            <Input readOnly placeholder="Collingwood" value={city} onChange={(e) => setCity(e.target.value)} />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider style={{ margin: '10px 0', backgroundColor: '#f5f5f5' }} />

                <Row align="middle" gutter={16}>
                    <Col span={8}>
                        <Text strong style={{fontFamily: "Inter", color: "#344054"}}>State / Province <span style={{ color: "#7F56D9" }}>*</span></Text>
                    </Col>
                    <Col span={10}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item>
                                    <Input readOnly placeholder="VIC" value={state} onChange={(e) => setState(e.target.value)} />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item>
                                    <Input readOnly placeholder="3066" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Divider style={{ margin: '10px 0', backgroundColor: '#f5f5f5' }} />

                {/* Country */}
                <Row align="middle" gutter={16}>
                    <Col span={8}>
                        <Text strong style={{fontFamily: "Inter", color: "#344054"}}>Country <span style={{ color: "#7F56D9" }}>*</span></Text>
                    </Col>
                    <Col span={10}>
                        <Form.Item>
                            <Select defaultValue="Australia" value={cardMethod.card.country} disabled>
                                <Option value="Australia">🇦🇺 Australia</Option>
                                <Option value="US">🇺🇸 United States</Option>
                                <Option value="India">🇮🇳 India</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                {/* Submit Button */}
                <Row justify="center">
                    <Col span={16}>
                        <Button type="primary" block style={{ background: "#7F56D9", maxWidth: "25%", marginLeft: "35%", fontFamily: "Inter" }} onClick={editCardDetails}>
                            {type === "edit" ? "Update" : "Add"} Payment Method
                        </Button>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default SubscriptionEditPaymentMethod;